import { motion } from 'framer-motion';
import { Player } from '@/lib/gameStore';
import { useEffect, useState, useCallback, useRef } from 'react';
import { useSound } from '@/hooks/useSound';
import { useHaptic } from '@/hooks/useHaptic';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronRight } from 'lucide-react';

interface RoundResultsProps {
  selections: any[];
  players: Player[];
  previousTotal: number;
  newTotal: number;
  targetTotal: number;
  onClose: () => void;
}

export default function RoundResults({ 
  selections, 
  players, 
  previousTotal, 
  newTotal, 
  targetTotal,
  onClose 
}: RoundResultsProps) {
  const { playSound } = useSound();
  const { vibrate } = useHaptic();
  const [revealedIndex, setRevealedIndex] = useState(-1);
  const [runningTotal, setRunningTotal] = useState(previousTotal);
  const [showFinalResult, setShowFinalResult] = useState(false);
  const hasClosedRef = useRef(false);
  const onCloseRef = useRef(onClose);
  
  // Keep ref updated
  onCloseRef.current = onClose;

  const movedCloser = Math.abs(newTotal - targetTotal) < Math.abs(previousTotal - targetTotal);
  const isOvershoot = newTotal > targetTotal;
  const diff = newTotal - previousTotal;

  // Stable close handler
  const handleClose = useCallback(() => {
    if (hasClosedRef.current) return;
    hasClosedRef.current = true;
    onCloseRef.current();
  }, []);

  // Sequential reveal animation
  useEffect(() => {
    if (selections.length === 0) return;
    
    const revealDelay = 400;
    const perPlayerDelay = 600;
    const timers: NodeJS.Timeout[] = [];
    
    selections.forEach((selection, index) => {
      const timer = setTimeout(() => {
        setRevealedIndex(index);
        setRunningTotal(prev => prev + selection.selected_value);
        playSound('pop', 0.4);
        vibrate('selection');
      }, revealDelay + (index * perPlayerDelay));
      timers.push(timer);
    });
    
    // Show final result after all reveals
    const finalTimer = setTimeout(() => {
      setShowFinalResult(true);
      if (movedCloser) {
        playSound('success');
        vibrate('success');
      } else if (isOvershoot) {
        playSound('overshoot');
        vibrate('error');
      } else {
        playSound('failure');
        vibrate('warning');
      }
    }, revealDelay + (selections.length * perPlayerDelay) + 200);
    timers.push(finalTimer);
    
    // Auto close after animations
    const closeTimer = setTimeout(() => {
      handleClose();
    }, revealDelay + (selections.length * perPlayerDelay) + 2000);
    timers.push(closeTimer);

    return () => {
      timers.forEach(t => clearTimeout(t));
    };
  }, [selections.length, movedCloser, isOvershoot, playSound, vibrate, handleClose]);

  // Get color for running total
  const getTotalColor = () => {
    if (runningTotal === targetTotal) return 'text-chaos-green';
    if (runningTotal > targetTotal) return 'text-chaos-red';
    const distance = targetTotal - runningTotal;
    if (distance <= 5) return 'text-chaos-orange';
    if (distance <= 15) return 'text-chaos-yellow';
    return 'text-foreground';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="w-full max-w-md mx-auto"
    >
      <Card className="p-4 md:p-6 space-y-4 glass-card border-2 border-primary/30">
        {/* Running Total */}
        <div className="text-center">
          <div className="text-xs text-muted-foreground font-bold uppercase tracking-wider mb-1">
            Running Total
          </div>
          <motion.div
            key={runningTotal}
            initial={{ scale: 1.2 }}
            animate={{ scale: 1 }}
            className={`text-5xl md:text-6xl font-black ${getTotalColor()} transition-colors`}
          >
            {runningTotal}
          </motion.div>
        </div>

        {/* Player Selections - Compact Grid */}
        <div className="grid gap-2">
          {selections.map((selection, index) => {
            const player = players.find(p => p.id === selection.player_id);
            if (!player) return null;

            const isRevealed = index <= revealedIndex;

            return (
              <motion.div
                key={selection.id || `selection-${index}`}
                initial={{ opacity: 0.3, x: -20 }}
                animate={{ 
                  opacity: isRevealed ? 1 : 0.3,
                  x: isRevealed ? 0 : -20
                }}
                className={`flex items-center justify-between p-3 rounded-xl transition-all ${
                  isRevealed
                    ? 'glass-card border border-primary/30'
                    : 'bg-muted/30'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div 
                    className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                      isRevealed ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {player.name[0].toUpperCase()}
                  </div>
                  <span className="font-bold text-foreground text-sm">{player.name}</span>
                </div>
                
                {isRevealed ? (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className={`text-2xl font-black ${
                      selection.selected_value > 0 ? 'text-chaos-green' : 'text-chaos-red'
                    }`}
                  >
                    {selection.selected_value > 0 ? '+' : ''}{selection.selected_value}
                  </motion.span>
                ) : (
                  <span className="text-2xl text-muted-foreground/30 font-black">?</span>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Final Result */}
        {showFinalResult && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`p-3 rounded-xl text-center ${
              movedCloser 
                ? 'bg-chaos-green/20 border border-chaos-green' 
                : 'bg-chaos-red/20 border border-chaos-red'
            }`}
          >
            <div className="text-xl font-black">
              {movedCloser ? '🔥 CLOSER! 🔥' : '💀 UH OH 💀'}
            </div>
            <div className="text-sm text-muted-foreground mt-1">
              {previousTotal} → {newTotal} ({diff > 0 ? '+' : ''}{diff})
            </div>
          </motion.div>
        )}

        {/* Continue Button */}
        {showFinalResult && (
          <Button
            onClick={handleClose}
            className="w-full"
            size="lg"
          >
            Continue
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        )}

        {/* Progress bar for auto-close */}
        <motion.div
          className="h-1 bg-border rounded-full overflow-hidden"
          initial={{ opacity: 0 }}
          animate={{ opacity: showFinalResult ? 1 : 0.3 }}
        >
          <motion.div
            className={`h-full ${movedCloser ? 'bg-chaos-green' : 'bg-chaos-red'}`}
            initial={{ width: '100%' }}
            animate={{ width: showFinalResult ? '0%' : '100%' }}
            transition={{ duration: 1.8, ease: 'linear' }}
          />
        </motion.div>
      </Card>
    </motion.div>
  );
}
